import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Select, ConfigProvider, Empty, Card, Space, theme as antdTheme, Tooltip } from 'antd';

export interface RoleItem {
    // 已删除 licenseTypeId，只保留用户类型与角色
    // 原为 userType: string | number; 改为 userTypeId
    userTypeId: string | number;
    // 原为 roleId?: string | number | null; 改为数组以支持多选
    roleIds?: (string | number)[] | null;
}

export interface UserTypeOptions {
    id: string | number;
    name: string;
    [key: string]: any;
}

export interface RoleOptions {
    id: string | number;
    roleName: string;
    [key: string]: any;
}

export interface RoleSelectProps {
    // ===== 受控模式：传入 value 时组件完全受控 =====
    value?: string;
    // ===== 非受控模式：不传 value 时使用 defaultValue 作为初始值 =====
    defaultValue?: string;
    onChange?: (value: string) => void;
    // ★ 已删除 licenseTypes 属性
    // ★ 已删除 onLicenseTypesChange 回调（不再有权限类型切换）
    userTypes: UserTypeOptions[];
    roles: RoleOptions[];
    disabled?: boolean;
    theme?: { isDark?: boolean };
    requireAllRoles?: boolean;
    onValidationChange?: (isValid: boolean) => void;
    parseValue?: (value: string) => RoleItem[];
    formatValue?: (items: RoleItem[]) => string;
}

// ============ 默认函数放组件外 ============
const defaultParseValue = (val?: string): RoleItem[] => {
    if (!val || typeof val !== 'string') return [];
    try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const defaultFormatValue = (items: RoleItem[]): string => JSON.stringify(items);

const isDarkColor = (color?: string): boolean => {
    if (!color) return false;
    const hex = color.replace('#', '').slice(0, 6);
    if (hex.length !== 6) return false;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return false;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
};

// ★ 校验函数：不再按权限类型分组，直接检查每个用户类型是否都选了默认角色
export const validateRoleValue = (
    value: string | undefined,
    userTypes: UserTypeOptions[],
    requireAllRoles: boolean = false
): { valid: boolean; errorMessage?: string } => {
    if (!requireAllRoles) return { valid: true };
    const arr = defaultParseValue(value);
    const missing = (userTypes || []).filter(
        // 检查每个用户类型是否已配置至少一个角色
        (ut) => !arr.find((r) => r.userTypeId === ut.id && r.roleIds && r.roleIds.length > 0)
    );
    if (missing.length > 0) {
        return {
            valid: false,
            errorMessage: `用户类型 [${missing.map((m) => m.name).join('、')}] 还未选择默认角色`,
        };
    }
    return { valid: true };
};

const RoleSelect: React.FC<RoleSelectProps> = ({
    // 原默认值 value = '[]' 改为不设默认值，用 undefined 判断是否为受控
    value,
    // 新增：非受控模式的初始值
    defaultValue = '[]',
    onChange,
    // ★ 已删除 licenseTypes 解构
    userTypes = [],
    roles = [],
    disabled = false,
    theme = {},
    parseValue = defaultParseValue,
    formatValue = defaultFormatValue,
}) => {
    // ★ 是否受控：value 不为 undefined 即为受控模式
    const isControlled = value !== undefined;

    // ★ 非受控模式下的内部 state（初始取 defaultValue）
    const [innerValue, setInnerValue] = useState<string>(defaultValue);

    // ★ 实际用于派生的值：受控取 value，非受控取内部 state
    const mergedValue = isControlled ? (value as string) : innerValue;

    // 自动检测暗色
    const { token } = antdTheme.useToken();
    const autoIsDark = isDarkColor(token.colorBgContainer);
    const isDark = theme.isDark ?? autoIsDark;

    const formatValueRef = useRef(formatValue);
    formatValueRef.current = formatValue;

    // ★ 完全受控/非受控统一：data 从 mergedValue 派生
    const data = useMemo(() => parseValue(mergedValue), [mergedValue, parseValue]);

    // 归一化 options
    const userTypeOptions = useMemo(
        () => (userTypes || []).map((item) => ({ label: item.name, value: item.id })),
        [userTypes]
    );
    const roleOptions = useMemo(
        () => (roles || []).map((item) => ({ label: item.roleName, value: item.id })),
        [roles]
    );

    // ★ 统一变更出口：非受控时同步内部 state，两种模式都触发 onChange
    const emitChange = (newValue: string) => {
        if (!isControlled) {
            setInnerValue(newValue);
        }
        onChange?.(newValue);
    };

    // ★ 已删除权限类型相关的 useEffect（onLicenseTypesChange 回传逻辑）

    // 事件：直接 onChange 抛出新的完整 JSON，UI 由父组件回灌的 value 派生
    // (非受控模式下则由内部 state 派生)
    const handleRoleChange = (
        userType: string | number,
        selectedRoleIds: (string | number)[] | undefined // 参数改为数组
    ) => {
        const newData = [...data];
        const index = newData.findIndex(
            // 将 item.userType === userType 改为 item.userTypeId === userType
            (item) => item.userTypeId === userType
        );
        // 使用数组赋值
        const roleIds = selectedRoleIds ?? [];
        if (index > -1) {
            newData[index] = { ...newData[index], roleIds };
        } else {
            // 将 userType 改为 userTypeId
            newData.push({ userTypeId: userType, roleIds });
        }
        // ★ 走统一出口
        emitChange(formatValueRef.current(newData));
    };

    const getSelectedRole = (userType: string | number) => {
        const item = data.find(
            // 将 i.userType === userType 改为 i.userTypeId === userType
            (i) => i.userTypeId === userType
        );
        // 返回数组以适应多选 Select
        return item ? (Array.isArray(item.roleIds) ? item.roleIds : []) : [];
    };

    const selectTheme = isDark
        ? {
            token: {
                colorBgContainer: '#1f1f1f',
                colorBorder: '#434343',
                colorText: '#ffffff',
                colorPrimary: '#1890ff',
                colorBgElevated: '#1f1f1f',
            },
        }
        : undefined;

    if (userTypeOptions.length === 0) {
        return (
            <div style={{ padding: '16px 0' }}>
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <span style={{ color: isDark ? '#8c8c8c' : '#999' }}>
                            暂无用户类型数据
                        </span>
                    }
                />
            </div>
        );
    }

    return (
        <ConfigProvider theme={selectTheme}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {/* ★ 已删除权限类型多选 Select */}
                <Card
                    size="small"
                    title={
                        <span style={{ color: isDark ? '#1890ff' : '#1677ff', fontWeight: 600 }}>
                            新用户默认角色配置
                        </span>
                    }
                    style={{
                        backgroundColor: isDark ? '#1f1f1f' : '#fafafa',
                        borderColor: isDark ? '#434343' : '#d9d9d9',
                    }}
                    styles={{ body: { padding: 16 } }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '16px 24px',
                            width: '100%',
                        }}
                    >
                        {userTypeOptions.map((ut) => (
                            <div
                                key={ut.value}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: 'calc(50% - 12px)',
                                    minWidth: 240,
                                }}
                            >
                                <div
                                    style={{
                                        width: 80,
                                        flexShrink: 0,
                                        color: isDark ? '#a6a6a6' : '#666',
                                        fontSize: '14px',
                                        marginRight: 8,
                                    }}
                                >
                                    {ut.label}：
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Select
                                        mode="multiple" // ★ 多选模式
                                        style={{ width: '100%' }}
                                        placeholder="请选择默认角色"
                                        value={getSelectedRole(ut.value)}
                                        onChange={(val) => handleRoleChange(ut.value, val)}
                                        options={roleOptions}
                                        disabled={disabled}
                                        allowClear
                                        maxTagCount={1} // ★ 固定只显示第一个选中的标签
                                        // ★ 自定义折叠后的提示文案，显示 "+N..." 并带有 Tooltip
                                        maxTagPlaceholder={(omittedValues) => {
                                            // 提取被折叠角色的名称并拼接
                                            const names = omittedValues.map((item: any) => item.label).join('、');
                                            return (
                                                <Tooltip title={names}>
                                                    <span style={{ cursor: 'pointer' }}>+{omittedValues.length} ...</span>
                                                </Tooltip>
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </Space>
        </ConfigProvider>
    );
};

export default RoleSelect;